import React, { useEffect, useState } from "react";
import { ThemeProvider } from "@emotion/react";
import {
  Box,
  MenuItem,
  TableBody,
  TableCell,
  TableRow,
  Toolbar,
  createTheme,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import Moment from "react-moment";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import Loader from "../../../shared/components/Loader";
import ActiveDisabledStatus from "../../../shared/components/ActiveDisabledStatus";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import ActionToast from "../../../shared/components/ActionToast";
import { changeUserStatus, deleteUser, getUsers, selectUser, changeQuery, setSuccess } from "../../adminUsersSlice";
import { AdminUsersState, User, UsersQueryParameter } from "../../models/AdminUsersModels";
import UserDialog from "./UserDialog";
import { getName } from "../../../shared/models/models";
import Table from "../../../shared/components/tables/Table";
import SortTableHead, { TableColumnDefinition } from "../../../shared/components/SortTableHead";
import TablePagination from "../../../shared/components/tables/TablePagination";
import TableActionButton from "../../../shared/components/tables/TableActionButton";
import { useConfirmation } from "../../../shared/components/confirmation/ConfirmationProvider";
import AdminSearch from "../AdminSearch";
import { useRoleLabel } from "../../../customer/settings/utils/useRoleLabel";

const Users = () => {
  const { query, users, user, loading, success, error } = useAppSelector<AdminUsersState>(
    (state: any) => state.adminUsers
  );
  const dispatch = useAppDispatch();
  const confirm = useConfirmation();
  const getRoleLabel = useRoleLabel();
  const [isUserDialog, setIsUserDialog] = useState(false);

  const columns: TableColumnDefinition[] = [
    { title: "Role" },
    { title: "Email", parameter: UsersQueryParameter.email },
    { title: "Full name", parameter: UsersQueryParameter.fullName },
    { title: "Created", parameter: UsersQueryParameter.createdDate },
    { title: "Last activity", parameter: UsersQueryParameter.modifiedDate },
    { title: "Last login" },
    { title: "Status" },
    { title: undefined }
  ];

  const handleSearch = (value?: string) => {
    const newQuery = { ...query, search: value, pageNo: 1 };
    dispatch(changeQuery(newQuery));
    dispatch(getUsers(newQuery));
  };

  const handleSort = (parameter: number) => {
    const sortDesc = query.sortBy !== parameter ? false : !query.sortDesc;
    const newQuery = { ...query, sortBy: parameter, sortDesc: sortDesc };

    dispatch(changeQuery(newQuery));
    dispatch(getUsers(newQuery));
  };

  const handlePageSizeChange = async (size: number) => {
    const newQuery = { ...query, pageNo: 1, pageSize: size };
    await dispatch(changeQuery(newQuery));
    dispatch(getUsers(newQuery));
  };

  const handlePageNoChange = async (no: number) => {
    const newQuery = { ...query, pageNo: no };
    await dispatch(changeQuery(newQuery));
    dispatch(getUsers(newQuery));
  };

  const handleEdit = (user: User) => {
    dispatch(selectUser(user));
    setIsUserDialog(true);
  };

  const handleChangeStatus = async (user: User, isDisabled: boolean) => {
    if (user) {
      const isPendingApproval = user.isDisabled && !user.loginDate;
      await dispatch(changeUserStatus({ id: user.id, isDisabled }));
      const message = isDisabled
        ? "User disabled successfully"
        : isPendingApproval
          ? "User approved successfully"
          : "User activated successfully";
      dispatch(setSuccess(message));
      await dispatch(getUsers(query));
    }
  };

  const handleDelete = async (user: User) => {
    if (user) {
      await dispatch(deleteUser(user.id));
      await dispatch(getUsers(query));
    }
  };

  useEffect(() => {
    dispatch(getUsers(query));
  }, []);

  const theme = createTheme({
    components: {
      MuiToolbar: {
        styleOverrides: {
          root: {
            background: "#fff",
            borderBottom: "1px solid #d5d6d9"
          }
        }
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            boderRadius: "6px 0 0 6px !important"
          }
        }
      }
    }
  });

  if (loading) {
    return <Loader />;
  }

  return (
    <ThemeProvider theme={theme}>
      <ActionToast
        open={!!success}
        variant="success"
        message={success || ""}
        onClose={() => dispatch(setSuccess(undefined))}
        duration={6000}
      />

      <Toolbar className="flex items-center justify-between w-full">
        <div className="flex items-center">
          <p className="heading-2">Users</p>
          <div className="ml-7">
            <AdminSearch value={query.search} onSearch={handleSearch} />
          </div>
        </div>
      </Toolbar>

      <Table className="px-3 v-fill">
        <SortTableHead columns={columns} sortBy={query.sortBy} sortDesc={query.sortDesc} onSort={handleSort} />

        <TableBody>
          {users?.items?.map((user) => (
            <TableRow key={user.id}>
              <TableCell>{user.role ? getRoleLabel(user.role) : ""}</TableCell>

              <TableCell>{user.email}</TableCell>

              <TableCell>{[user.profile?.firstName, user.profile?.lastName].join(" ")}</TableCell>

              <TableCell sx={{ minWidth: "140px" }}>
                <Moment date={user.createdDate} format="DD.MM.YYYY HH:mm" />
              </TableCell>

              <TableCell sx={{ minWidth: "160px" }}>
                <Moment date={user.modifiedDate} format="DD.MM.YYYY HH:mm" />
              </TableCell>

              <TableCell sx={{ minWidth: "120px" }}>
                {user.loginDate ? <Moment date={user.loginDate} format="DD.MM.YYYY HH:mm" /> : ""}
              </TableCell>

              <TableCell sx={{ minWidth: "120px" }}>
                {user.isDisabled && !user.loginDate ? (
                  <Box sx={{ color: "#F57C00", display: "flex", alignItems: "center" }}>
                    <HourglassEmptyOutlinedIcon sx={{ height: "20px", width: "20px" }} />
                    <span className="text-14-medium ms-2" style={{ color: "#F57C00" }}>Pending Approval</span>
                  </Box>
                ) : (
                  <ActiveDisabledStatus disabled={user.isDisabled} />
                )}
              </TableCell>

              <TableCell>
                <TableActionButton>
                  {user.isEmailConfirmed && (
                    <MenuItem onClick={() => handleEdit(user)}>
                      <EditOutlinedIcon />
                      Edit
                    </MenuItem>
                  )}

                  {user.isEmailConfirmed && (
                    <Box>
                      {user?.isDisabled ? (
                        <MenuItem onClick={() => handleChangeStatus(user, false)}>
                          <CheckCircleOutlinedIcon />
                          {user.isDisabled && !user.loginDate ? "Approve" : "Enable"}
                        </MenuItem>
                      ) : (
                        <MenuItem onClick={() => handleChangeStatus(user, true)}>
                          <CancelOutlinedIcon />
                          Disable
                        </MenuItem>
                      )}
                    </Box>
                  )}

                  <MenuItem
                    onClick={() => confirm({
                      title: "Delete user",
                      text: `Are you sure you want to delete ${getName(user.profile?.firstName, user.profile?.lastName)} user?`,
                      confirmButton: "Yes, delete",
                      closeButton: "No",
                      severity: "error",
                      onConfirm: () => handleDelete(user)
                    })}>
                    <DeleteOutlinedIcon />
                    Delete
                  </MenuItem>
                </TableActionButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <TablePagination
        variant="bottom"
        pageNo={query.pageNo}
        pageSize={query.pageSize}
        totalCount={users?.totalCount}
        onPageNoChange={handlePageNoChange}
        onPageSizeChange={handlePageSizeChange}
      />

      {user && (
        <UserDialog
          key={`user-dialog-${isUserDialog}`}
          open={isUserDialog}
          onClose={() => {
            setIsUserDialog(false);
            dispatch(getUsers(query));
          }}
          user={user}
        />
      )}
    </ThemeProvider>
  );
};

export default Users;